import * as THREE from "three";

export class Input {
  vec = new THREE.Vector2(); // x: lateral [-1,1], y: vertical [-1,1]
  boost = false;
  /** External override (e.g. virtual joystick). When set, takes priority. */
  externalVec: { x: number; y: number } | null = null;
  private keys = new Set<string>();
  private pointerActive = false;
  private pointer = new THREE.Vector2();
  private el: HTMLElement;

  constructor(el: HTMLElement) {
    this.el = el;
    window.addEventListener("keydown", this.onKey);
    window.addEventListener("keyup", this.onKeyUp);
    el.addEventListener("pointerdown", this.onPointerDown);
    el.addEventListener("pointermove", this.onPointerMove);
    window.addEventListener("pointerup", this.onPointerUp);
    el.addEventListener("pointerleave", this.onPointerUp);

    // iOS Safari pinch suppression on the canvas only — preserves pinch-zoom
    // elsewhere on the page for accessibility. These are iOS-specific
    // non-standard events; addEventListener is a no-op on other browsers.
    el.addEventListener("gesturestart", this.swallow, { passive: false });
    el.addEventListener("gesturechange", this.swallow, { passive: false });
    el.addEventListener("gestureend", this.swallow, { passive: false });
    // Belt + suspenders: kill the double-tap-to-zoom that Safari does even
    // when touch-action says otherwise, by preventing the second tap.
    let lastTap = 0;
    el.addEventListener("touchend", (e: Event) => {
      const now = Date.now();
      if (now - lastTap < 350) (e as Event).preventDefault();
      lastTap = now;
    }, { passive: false });
  }

  private swallow = (e: Event) => e.preventDefault();

  dispose() {
    window.removeEventListener("keydown", this.onKey);
    window.removeEventListener("keyup", this.onKeyUp);
    this.el.removeEventListener("pointerdown", this.onPointerDown);
    this.el.removeEventListener("pointermove", this.onPointerMove);
    window.removeEventListener("pointerup", this.onPointerUp);
    this.el.removeEventListener("pointerleave", this.onPointerUp);
    this.el.removeEventListener("gesturestart", this.swallow);
    this.el.removeEventListener("gesturechange", this.swallow);
    this.el.removeEventListener("gestureend", this.swallow);
  }

  /** Last activity timestamp (ms since page load). Read by game.ts for
   *  inactivity auto-pause. Set by any genuine input event. */
  lastActivityMs = performance.now();

  private onKey = (e: KeyboardEvent) => {
    if (e.target instanceof HTMLInputElement) return;
    this.keys.add(e.code);
    if (e.code === "Space") this.boost = true;
    this.lastActivityMs = performance.now();
  };
  private onKeyUp = (e: KeyboardEvent) => {
    this.keys.delete(e.code);
    if (e.code === "Space") this.boost = false;
    this.lastActivityMs = performance.now();
  };
  private onPointerDown = (e: PointerEvent) => {
    this.pointerActive = true;
    this.updatePointer(e);
    this.lastActivityMs = performance.now();
  };
  private onPointerMove = (e: PointerEvent) => {
    if (this.pointerActive) {
      this.updatePointer(e);
      this.lastActivityMs = performance.now();
    }
  };
  private onPointerUp = () => {
    this.pointerActive = false;
  };
  private updatePointer(e: PointerEvent) {
    const r = this.el.getBoundingClientRect();
    this.pointer.set(
      ((e.clientX - r.left) / r.width) * 2 - 1,
      -(((e.clientY - r.top) / r.height) * 2 - 1),
    );
  }

  poll(out: THREE.Vector2) {
    let x = 0,
      y = 0;
    if (this.keys.has("KeyA") || this.keys.has("ArrowLeft")) x -= 1;
    if (this.keys.has("KeyD") || this.keys.has("ArrowRight")) x += 1;
    if (this.keys.has("KeyW") || this.keys.has("ArrowUp")) y += 1;
    if (this.keys.has("KeyS") || this.keys.has("ArrowDown")) y -= 1;
    if (this.pointerActive) {
      x = this.pointer.x;
      y = this.pointer.y;
    }
    if (this.externalVec) {
      x = this.externalVec.x;
      y = this.externalVec.y;
      // Touch / gyro counts as activity too.
      if (x !== 0 || y !== 0) this.lastActivityMs = performance.now();
    }
    // clamp to unit circle for fairness
    const len = Math.hypot(x, y);
    if (len > 1) {
      x /= len;
      y /= len;
    }
    out.set(x, y);
    this.vec.copy(out);
  }
}
